import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { HeroIllustration } from '../components/mte/hero-asset-img';
import { Layout, Header, Card, Button } from '../components/mte/shell';
import { HERO_CELLS, type HeroCell, type HeroId } from '../components/mte/placeholders';
import type { Question } from '../data/banque-questions-ce1.legacy';
import {
  loadProgress,
  saveProgress,
  selectQuestionsForStep,
  TOTAL_QUESTIONS_PER_STEP,
  type AnswerRecord,
} from '../lib/mission-progress';
import { getActiveProfile, type Profile } from '../lib/profiles';
import styles from './MissionEtape.module.css';

/**
 * MissionEtape - exercice interactif d une etape de la Mission Rentree.
 * Profile : actif via profiles lib (multi-profile support).
 */

const HERO_BY_ID: ReadonlyMap<HeroId, HeroCell> = new Map(
  HERO_CELLS.filter((c) => c.id !== 'sage').map((c) => [c.id, c]),
);

function getHero(id: HeroId | null): HeroCell {
  if (!id) return HERO_CELLS[0] as HeroCell;
  return HERO_BY_ID.get(id) ?? (HERO_CELLS[0] as HeroCell);
}

function shuffleAnswers(question: Question): string[] {
  return [...question.options];
}

function encouragement(score: number, total: number): string {
  const ratio = score / total;
  if (ratio >= 0.9) return 'Bravo, tu maîtrises tes sorts !';
  if (ratio >= 0.7) return 'Très bien, tu progresses vite !';
  if (ratio >= 0.5) return 'Bon courage, tu peux continuer !';
  return 'Continue, chaque essai te rapproche';
}

function simplifyFeedback(raw: string | undefined): string {
  if (!raw) return 'Continue, tu vas y arriver !';
  return raw
    .replace(/est la forme conjugu[eé]e de l'infinitif/g, "est le verbe conjugué. L’infinitif est")
    .replace(/Avec sujet pluriel/g, 'Quand il y a plusieurs')
    .replace(/Ordre Sujet \+ Verbe \+ Compl[eé]ment/g, 'Ordre : Phrase + Verbe + Fin')
    .replace(/se termine par le son/g, 'a le son')
    .replace(/se conjugue/g, 'devient')
    .replace(/irr[eé]gulier/g, 'spécial')
    .replace(/grammaticalement correcte/g, 'correcte');
}

export default function MissionEtape(): JSX.Element {
  const [profile] = useState<Profile | null>(() => getActiveProfile());
  const hero = getHero(profile?.heroId ?? null);
  const [currentStepNumber, setCurrentStepNumber] = useState<number>(1);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<AnswerRecord[]>([]);
  const [selectedReponse, setSelectedReponse] = useState<string | null>(null);
  const [phase, setPhase] = useState<'loading' | 'question' | 'done'>('loading');

  // Charger progress + selectionner questions au mount (depends on profile).
  useEffect(() => {
    const active = profile ?? getActiveProfile();
    if (!active) {
      setPhase('loading');
      return;
    }
    const progress = loadProgress(active.id);
    setCurrentStepNumber(progress.currentStep);
    setQuestions(selectQuestionsForStep(active.id, progress.currentStep, active.schoolLevel));
    setIndex(0);
    setAnswers([]);
    setSelectedReponse(null);
    setPhase('question');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  // Toujours declarer currentQuestion + options (hooks rules: ordre stable).
  const currentQuestion = questions[index];
  const options = useMemo<string[]>(() => {
    if (!currentQuestion) return [];
    return shuffleAnswers(currentQuestion);
  }, [currentQuestion]);

  const repondu = selectedReponse !== null;
  const isCorrect = !!currentQuestion && repondu && selectedReponse === currentQuestion.bonneReponse;

  function handleChoose(option: string): void {
    if (repondu) return;
    setSelectedReponse(option);
  }

  function handleNext(): void {
    if (!repondu) return;
    if (!currentQuestion) return;
    const correct = selectedReponse === currentQuestion.bonneReponse;
    const record: AnswerRecord = {
      questionId: currentQuestion.id,
      reponse: selectedReponse,
      correct,
    };
    const nextAnswers = [...answers, record];
    const active = profile ?? getActiveProfile();
    if (!active) {
      // No active profile : cant persist. Stay on this question.
      return;
    }
    if (index + 1 >= questions.length) {
      const progress = loadProgress(active.id);
      const completedSet = new Set(progress.completedSteps);
      completedSet.add(currentStepNumber);
      const updatedCompleted = Array.from(completedSet).sort((a, b) => a - b);
      const scoreIncr = nextAnswers.filter((a) => a.correct).length;
      saveProgress(active.id, {
        currentStep:
          currentStepNumber < 15
            ? currentStepNumber + 1
            : currentStepNumber,
        completedSteps: updatedCompleted,
        stepResults: {
          ...progress.stepResults,
          [currentStepNumber]: {
            stepId: currentStepNumber,
            matiere: currentQuestion.matiere,
            answers: nextAnswers,
            score: scoreIncr,
            completedAt: new Date().toISOString(),
            selectedOrder: questions.map((q) => q.id),
          },
        },
        totalScore: progress.totalScore + scoreIncr,
      });
      setAnswers(nextAnswers);
      setPhase('done');
      return;
    }
    setAnswers(nextAnswers);
    setIndex(index + 1);
    setSelectedReponse(null);
  }

  const appBar = (
    <Header
      context="enfant"
      user={
        <div className={styles.appBar} data-testid="app-bar">
          <div className={styles.appBarIdentity}>
            <HeroIllustration cell={hero} pose="idle" />
            <p className={styles.appBarHello}>
              <span className={styles.appBarHelloAccent}>{profile?.firstName ?? 'Aventurier'}</span>
              {' . '}
              Etape {currentStepNumber} sur 15
            </p>
          </div>
          <Link
            to="/enfant/aventure/"
            className={styles.sortieLink}
            data-action="sortie-parent"
            aria-label="Sortie parent"
          >
            <Button tone="ghost" size="md" context="enfant">
              Sortie parent
            </Button>
          </Link>
        </div>
      }
    />
  );

  if (!profile) {
    return (
      <Layout context="adulte" header={undefined} width="narrow">
        <main className={styles.loadingScreen} data-testid="loading-screen">
          <p>Aucun profil actif. <Link to="/famille/accueil/">Choisis un profil</Link> pour commencer.</p>
        </main>
      </Layout>
    );
  }

  if (phase === 'loading') {
    return (
      <Layout context="enfant" header={appBar} width="child">
        <div className={styles.loadingScreen} data-testid="loading-screen">
          <p>Chargement de l etape...</p>
        </div>
      </Layout>
    );
  }

  if (phase === 'done') {
    const score = answers.filter((a) => a.correct).length;
    const encouragementMsg = encouragement(score, TOTAL_QUESTIONS_PER_STEP);
    return (
      <Layout context="enfant" header={appBar} width="child">
        <div className={styles.eyebrowRow}>
          <p className={styles.eyebrow}>Etape {currentStepNumber} terminee</p>
          <p className={styles.screenLabel} aria-label="Ecran 9b sur 11">9b / 11</p>
        </div>
        <section className={styles.heroBlock} aria-labelledby="etape-done-title">
          <div className={styles.heroIllustration} aria-hidden="true">
            <HeroIllustration cell={hero} pose="celebrate" />
          </div>
          <h1 id="etape-done-title" className={styles.doneTitle}>
            {encouragementMsg}
          </h1>
          <p
            className={styles.scoreLine}
            data-testid="etape-score"
            aria-live="polite"
          >
            {score} / {TOTAL_QUESTIONS_PER_STEP}
          </p>
          <p className={styles.scoreNote}>
            Ton score est sauvegarde. Tu peux revenir a la carte.
          </p>
        </section>
        <Card>
          <div className={styles.actions}>
            <Link
              to="/enfant/mission/rentree/carte/"
              data-action="retour-carte-apres-etape"
            >
              <Button tone="primary" size="xl" context="enfant" block>
                Retour à la carte
              </Button>
            </Link>
          </div>
        </Card>
      </Layout>
    );
  }

  if (!currentQuestion) {
    return (
      <Layout context="enfant" header={appBar} width="child">
        <div className={styles.loadingScreen}>
          <p>Aucune question disponible pour cette etape.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout context="enfant" header={appBar} width="child">
      <div className={styles.eyebrowRow}>
        <p className={styles.eyebrow}>Question {index + 1} / {questions.length}</p>
        <p className={styles.screenLabel} aria-label="Ecran 9b sur 11">9b / 11</p>
      </div>
      <section
        className={styles.questionBlock}
        aria-labelledby="etape-question-title"
      >
        <p
          className={styles.matiereTag}
          data-matiere={currentQuestion.matiere}
        >
          {currentQuestion.matiere === 'FR' ? 'Français' : 'Mathématiques'}
        </p>
        <h1
          id="etape-question-title"
          className={styles.questionTitle}
          data-testid="question-title"
        >
          {currentQuestion.enonce}
        </h1>
        <ul className={styles.optionsList} data-testid="options-list">
          {options.map((option) => {
            const isThisSelected = selectedReponse === option;
            const isCorrectOption = option === currentQuestion.bonneReponse;
            let feedback: 'correct' | 'incorrect' | null = null;
            if (repondu) {
              if (isThisSelected && isCorrectOption) feedback = 'correct';
              else if (isThisSelected && !isCorrectOption) feedback = 'incorrect';
              else if (!isThisSelected && isCorrectOption) feedback = 'correct';
            }
            return (
              <li key={option} className={styles.optionItem}>
                <button
                  type="button"
                  className={styles.optionButton}
                  data-testid="option"
                  data-option={option}
                  data-feedback={feedback ?? undefined}
                  disabled={repondu}
                  onClick={() => handleChoose(option)}
                  aria-pressed={isThisSelected}
                >
                  {option}
                </button>
              </li>
            );
          })}
        </ul>

        {repondu && (
          <div
            className={ (styles.feedback) + ' ' + (isCorrect ? styles.feedbackOk : styles.feedbackKo) }
            data-testid="feedback"
            aria-live="polite"
          >
            <span className={styles.feedbackBadge}>
              {isCorrect ? 'Bien joué !' : 'Pas tout à fait'}
            </span>
            <p className={styles.feedbackText}>{simplifyFeedback(currentQuestion.explication)}</p>
          </div>
        )}
      </section>
      <Card>
        <div className={styles.actions}>
          <Button
            tone="primary"
            size="xl"
            context="enfant"
            block
            disabled={!repondu}
            onClick={handleNext}
            data-action={index + 1 >= questions.length ? 'terminer-etape' : 'question-suivante'}
            data-testid={index + 1 >= questions.length ? 'terminer-etape' : 'question-suivante'}
          >
            {index + 1 >= questions.length ? 'Terminer l étape' : 'Question suivante'}
          </Button>
        </div>
      </Card>
    </Layout>
  );
}
